import { getCurrentScope, applyScopeDataToEvent } from '@sentry/core';
import type { Event, EventHint, Integration, ScopeContext } from '@sentry/core';

export function sentryErrorIntegration(): Integration {
   return {
      name: 'SentryErrorIntegration',
      processEvent(event: Event, hint: EventHint) {
         const exception = hint.originalException;

         // Duck-typed rather than `instanceof SentryError`: Next.js bundles
         // `@ifixit/sentry` separately for the server runtime and the RSC
         // runtime, so the `SentryError` class identity used at throw time
         // can differ from the one imported here, making `instanceof` fail.
         if (isSentryErrorLike(exception)) {
            const newScope = getCurrentScope().clone();
            newScope.update(exception.sentryDetails);
            applyScopeDataToEvent(event, newScope.getScopeData());
         }

         return event;
      },
   };
}

export function isSentryErrorLike(
   value: unknown
): value is Error & { sentryDetails: Partial<ScopeContext> } {
   if (!(value instanceof Error) || !('sentryDetails' in value)) {
      return false;
   }
   const details = (value as { sentryDetails?: unknown }).sentryDetails;
   return typeof details === 'object' && details !== null;
}

export class SentryError extends Error {
   constructor(
      message: string,
      readonly sentryDetails: Partial<ScopeContext> = {}
   ) {
      super(message);
   }
}
